import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import { CallRecordingResponse, CallRecordingsListQueryParams, PaginatedResponse } from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

interface CacheEntry {
  data: PaginatedResponse<CallRecordingResponse>;
  timestamp: number;
}

interface CallRecordingsCache {
  [cacheKey: string]: CacheEntry;
}

interface StoreInterface {
  // Current view state
  currentData: CallRecordingResponse[];

  // Offset pagination state
  page: number;
  totalPages: number;
  total: number;
  limit: number;

  // Sorting (server-side, maps to CallRecordingsListQueryParams.sort)
  sort: string[];

  // Search (server-side, maps to CallRecordingsListQueryParams.search)
  search: string;

  // User filter — when set, scopes results to a single user
  userId?: number;

  // Loading states
  isLoading: boolean;
  error?: string;

  // Cache
  _cache: CallRecordingsCache;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_LIMIT = 20;
const DEFAULT_SORT: string[] = ["createdAt:desc"];

const BlankState: StoreInterface = {
  currentData: [],

  page: 1,
  totalPages: 1,
  total: 0,
  limit: DEFAULT_LIMIT,

  sort: DEFAULT_SORT,

  search: "",

  isLoading: false,
  error: undefined,

  _cache: {}
};

function createCacheKey(params: CallRecordingsListQueryParams): string {
  const offset = params.offset ?? 0;
  const limit = params.limit ?? DEFAULT_LIMIT;
  const sortKey = (params.sort ?? DEFAULT_SORT).join(",");
  const locationKey = params.locationId !== undefined ? String(params.locationId) : "all";
  const searchKey = params.search?.trim() ?? "";
  const userKey = params.userId !== undefined ? String(params.userId) : "all";

  return `offset:${offset}|limit:${limit}|sort:${sortKey}|location:${locationKey}|search:${searchKey}|user:${userKey}`;
}

function isCacheStale(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp > CACHE_TTL_MS;
}

export default class CallRecordingsModel {
  static useState = create<StoreInterface>(() => ({ ...BlankState }));
  static setState = CallRecordingsModel.useState.setState;

  static get state() {
    return CallRecordingsModel.useState.getState();
  }

  static reset() {
    CallRecordingsModel.setState({ ...BlankState });
  }

  private static getInitialQueryParams(): CallRecordingsListQueryParams {
    const { limit, page, sort, search, userId } = CallRecordingsModel.state;
    const offset = (page - 1) * limit;
    const params: CallRecordingsListQueryParams = {
      limit,
      offset,
      sort
    };
    if (search.trim()) params.search = search.trim();
    if (userId !== undefined) params.userId = userId;
    return params;
  }

  private static getFromCache(params: CallRecordingsListQueryParams): PaginatedResponse<CallRecordingResponse> | null {
    const key = createCacheKey(params);
    const entry = CallRecordingsModel.state._cache[key];

    if (!entry) {
      return null;
    }

    if (isCacheStale(entry)) {
      return null;
    }

    return entry.data;
  }

  private static setInCache(params: CallRecordingsListQueryParams, data: PaginatedResponse<CallRecordingResponse>) {
    const key = createCacheKey(params);
    const newCache = {
      ...CallRecordingsModel.state._cache,
      [key]: {
        data,
        timestamp: Date.now()
      }
    };
    CallRecordingsModel.setState({ _cache: newCache });
  }

  static invalidateCache() {
    CallRecordingsModel.setState({ _cache: {} });
  }

  static async load(forceRefresh: boolean = false) {
    const params = CallRecordingsModel.getInitialQueryParams();

    if (!forceRefresh) {
      const cached = CallRecordingsModel.getFromCache(params);
      if (cached) {
        CallRecordingsModel.setState({
          currentData: cached.data,
          page: cached.pagination.page,
          totalPages: cached.pagination.totalPages,
          total: cached.pagination.total
        });
        return;
      }
    }

    CallRecordingsModel.setState({ isLoading: true, error: undefined });

    try {
      const response = await API.Calls.getCallRecordingsListByOrg(Member.orgId!, params);

      CallRecordingsModel.setInCache(params, response);

      CallRecordingsModel.setState({
        currentData: response.data,
        page: response.pagination.page,
        totalPages: response.pagination.totalPages,
        total: response.pagination.total,
        isLoading: false
      });
    } catch (error) {
      warn("Error loading call recordings", error);
      CallRecordingsModel.setState({
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to load recordings"
      });
    }
  }

  static async setFilters(filters: { limit?: number }) {
    CallRecordingsModel.setState({
      limit: filters.limit ?? CallRecordingsModel.state.limit,
      page: 1,
      totalPages: 1,
      total: 0
    });

    await CallRecordingsModel.load();
  }

  static async setLimit(limit: number) {
    await CallRecordingsModel.setFilters({ limit });
  }

  static async setSearch(search: string) {
    const nextSearch = search?.trim() ?? "";
    const { search: currentSearch } = CallRecordingsModel.state;

    if (nextSearch === currentSearch) {
      return;
    }

    CallRecordingsModel.setState({
      search: nextSearch,
      page: 1,
      totalPages: 1,
      total: 0
    });

    await CallRecordingsModel.load(true);
  }

  static async setSorting(sort: string[]) {
    const nextSort = sort.length > 0 ? sort : DEFAULT_SORT;
    const { sort: currentSort } = CallRecordingsModel.state;

    const isSameSort = currentSort.length === nextSort.length && currentSort.every((value, index) => value === nextSort[index]);

    if (isSameSort) {
      return;
    }

    CallRecordingsModel.setState({
      sort: nextSort,
      page: 1,
      totalPages: 1,
      total: 0
    });

    await CallRecordingsModel.load(true);
  }

  static async setPage(page: number) {
    const { totalPages } = CallRecordingsModel.state;
    const validPage = Math.max(1, Math.min(page, totalPages));

    if (validPage !== CallRecordingsModel.state.page) {
      CallRecordingsModel.setState({ page: validPage });
      await CallRecordingsModel.load(true);
    }
  }
}
